import { call, take, takeLatest, takeEvery, put, fork, select } from 'redux-saga/effects'
import { revenue, Revenue } from '@amplitude/analytics-browser'

import { indicateProgress } from 'api'
import { fetchLocationByGrsId, fetchLocationById } from 'sfproxy/api'
import * as grs from 'grs'
import getRandomness from 'shared/random-generator'
import featureToggles from 'shared/featureToggles'
import { getRecaptchaToken } from 'shared/recaptcha'
import { PIX_PAYMENT_METHOD, PSE_PAYMENT_METHOD, GOOGLE_PAY_METHOD, getPaymentMethod } from 'shared/transactions'
import { REMOVE_VEHICLE } from 'dashboard-vehicles/constants'
import { SHOW_MONTHLY_FLOW, SHOW_PREPAID_FLOW, FETCH_EVENT_RATE } from 'booking/constants'
import { SET_EVENT } from 'search/constants'
import { addEventDetails } from 'search/actions'
import { searchDateTimeSelector } from 'search-date-time'
import { languageSelector, apiLanguageCodeSelector } from 'i18n/selectors'
import { userSelector, userAccountIdSelector } from 'auth/selectors'
import { refreshAuthenticatedUser } from 'auth/actions'
import { initPayment, setPixQRCodeData } from 'credit-cards/actions'
import { PAYMENT_SUCCESS, PAYMENT_ERROR, PAYMENT_CANCELLED } from 'credit-cards/constants'
import { LOCATION_CHANGE } from 'browser-location'
import { getReservationConfirmation } from 'confirmation/actions'
import { GET_RESERVATION_CONFIRMATION_RESPONSE } from 'confirmation/constants'
import { reservationByIdSelector } from 'dashboard-transactions/selectors'
import { sendGoogleReserveConversionData } from 'analytics/actions'

import * as constants from './constants'
import * as actions from './actions'
import * as api from './api'
import {
    rateSelector,
    selectedRateIdSelector,
    selectedVehicleSelector,
    rawPromoCodeSelector,
    currentCustomerFlowSelector,
    locationDetailsSelector,
    selectedCreditCardSelector,
    selectedUpSellOptionSelector,
    selectedVehicleDetailsSelector,
    guestUserCardVerifiedSelector,
} from './selectors'
import { RatesError, CreateReservationError } from './errors'
import { createDenormalizedSelector } from 'entities/selectors'
import { rateSchema } from 'grs/schemas'
import { customerFlowType } from 'grs/helpers'
import { searchModeSelector } from 'map/selectors'
import { redeemPromoCode } from './api'
import { requestPayment, extractGooglePayFields } from 'credit-cards/google-pay/service'
import { loadGooglePayScript } from 'credit-cards/google-pay/service'
import { getAnalyticsFlowType, getRwgToken, isAmplitudeInitialized, sendGTAGEvent } from 'analytics/utils'

function* getGooglePayFieldsForBookingPurchase(paymentTotal) {
    yield call(loadGooglePayScript)
    const { merchantId } = yield select(locationDetailsSelector)
    const paymentData = yield call(requestPayment, merchantId, paymentTotal)
    const googlePayFields = extractGooglePayFields(paymentData)

    if (!googlePayFields) {
        console.error('Missing Google Pay token')
        return
    }

    return googlePayFields
}

const locationRatesPredicate = (requestId, action) =>
    [grs.constants.RECEIVED_LOCATION_RATES, grs.constants.FAILED_TO_FETCH_RATES].indexOf(action.type) !== -1 &&
    action.requestId === requestId

function* getRates({ grsId, preselectedRateId, extendingStart, extendingEnd, isExtending }) {
    const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))
    const currentCustomerFlow = yield select(currentCustomerFlowSelector)

    yield put(actions.setRates(null))
    const requestId = getRandomness()
    const { start: formStart, end: formEnd } = yield select(searchDateTimeSelector)
    const beginTime = extendingStart || formStart
    const endTime = extendingEnd || formEnd
    yield put(
        grs.actions.fetchRates(
            [{ grsId, beginTime, endTime, preselectedRateId, isExtending }],
            requestId,
            currentCustomerFlow || currentCustomerFlowType
        )
    )
    const { result, type, error } = yield take(locationRatesPredicate.bind(null, requestId))
    if (type === grs.constants.FAILED_TO_FETCH_RATES) throw new RatesError(error.message)

    const $rateId = yield select(selectedRateIdSelector)

    if (preselectedRateId || $rateId) {
        let rateId = preselectedRateId

        if (!rateId) {
            const selectedRate = yield select(createDenormalizedSelector($rateId, rateSchema))
            rateId = selectedRate.rateId
        }
        const receivedRates = yield select(createDenormalizedSelector(result, [rateSchema]))

        const matchingRate = receivedRates.find(rate => rate.rateId === rateId)

        if (matchingRate) yield put(actions.selectRate(matchingRate.$rateId))
    }

    yield put(actions.setRates(result))
}

export function* watchGetRates() {
    yield takeLatest(constants.GET_RATES, function* (action) {
        try {
            yield call(indicateProgress, 'bookingRates', getRates, action)
        } catch (error) {}
    })
}

export function* watchSelectRate() {
    yield takeEvery(constants.SELECT_RATE, function* () {
        const { numberOfVehicles } = yield select(rateSelector)
        const selectedVehicles = yield select(selectedVehicleSelector)

        if (numberOfVehicles && numberOfVehicles < selectedVehicles.length)
            yield put(actions.selectVehicle(selectedVehicles.slice(0, numberOfVehicles)))
    })
}

export function* watchSetEvent() {
    const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))

    yield takeEvery(SET_EVENT, function* ({ event }) {
        const { fromDate, toDate, locationId, rateId } = event
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            const { rateTable } = yield call(grs.api.getRateDetails, {
                locationId,
                rateId,
                fromDate,
                toDate,
                waitingListKey: null,
                apiLanguageCode,
                customerFlowType: currentCustomerFlowType,
            })
            yield put(addEventDetails({ rateTable }))
        } catch (error) {}
    })
}

export function* watchFetchEventRate() {
    yield takeEvery(FETCH_EVENT_RATE, function* ({ locationId, rateId, eventId }) {
        yield call(indicateProgress, { names: ['eventRate'], clearPending: true }, function* () {
            try {
                const apiLanguageCode = yield select(languageSelector)
                const { rateTable } = yield call(grs.api.getEventMultipleRate, {
                    locationId,
                    rateId,
                    eventId,
                    apiLanguageCode,
                })
                yield put(addEventDetails({ rateTable }))
            } catch (error) {}
        })
    })
}

export function* watchRemoveVehicle() {
    yield takeEvery(REMOVE_VEHICLE, function* ({ vehicle }) {
        yield put(actions.removeFromSelectedVehicles(vehicle))
    })
}

// TODO Additional data cleanup is need as we do not rely on SF that much anymore
export function* getBookingLocationDetails({ grsId }) {
    const language = yield select(languageSelector)

    try {
        const locationDetails = yield call(fetchLocationByGrsId, grsId, language)
        const locationDataBind = yield call(grs.api.getDefaultCustomerFlow, [grsId], language)
        const locationDataFields = yield call(grs.api.getLocationDataFields, grsId, language)
        const defaultCustomerFlow = locationDataBind.defaultCustomerFlow

        if (locationDetails || locationDataBind) {
            const locationId = locationDetails.result

            let customerFlow = defaultCustomerFlow
            if (featureToggles.caPurchaseFlow) {
                customerFlow = defaultCustomerFlow
                    .split('|')
                    .filter(flow => flow !== SHOW_PREPAID_FLOW[0] && flow !== SHOW_MONTHLY_FLOW[0])
                    .join('|')
            }
            yield put(
                actions.setBookingLocationDetails({
                    ...(locationDetails ? locationDetails.entities.locations[locationId] : {}),
                    ...locationDataBind,
                    dataFields: locationDataFields,
                    defaultCustomerFlow: customerFlow,
                    timeZone: locationDataBind.timeZoneIdentifier,
                    merchantId: locationDataBind.merchantID,
                })
            )
        }
    } catch (e) {
        // Swallow error to prevent blocking UI
    }
}

export function* getBookingLocationDetailsMonthlyParking() {
    yield takeEvery(constants.GET_LOCATION_DATA_BY_ID, function* ({ grsId }) {
        const language = yield select(languageSelector)

        try {
            const locationDetails = yield call(fetchLocationById, grsId, language)
            if (locationDetails) {
                const locationId = locationDetails.result
                yield put(
                    actions.setBookingLocationDetails({
                        ...locationDetails.entities.locations[locationId],
                    })
                )
            }
        } catch (e) {
            // Swallow error to prevent blocking UI
        }
    })
}

export function* watchGetBookingLocationDetails() {
    yield takeEvery(constants.GET_BOOKING_LOCATION_DETAILS, function* (action) {
        try {
            yield call(indicateProgress, 'locationDetails', getBookingLocationDetails, action)
        } catch (error) {}
    })
}

export function* promoCodeSaga() {
    yield takeLatest(constants.REDEEM_PROMO_CODE, function* ({ item }) {
        try {
            const rawPromoCode = yield select(rawPromoCodeSelector)
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const user = yield select(userSelector)

            const result = yield call(
                indicateProgress,
                `redeemPromoCode${item.id}`,
                redeemPromoCode,
                item,
                rawPromoCode,
                apiLanguageCode,
                user?.token
            )

            yield put(actions.redeemedPromoCode(result))
        } catch (exception) {}
    })
}

// TODO Would be good to move purchase related sagas, reducers and etc to separate folder.
// These are used by other flows besides booking flow as well.
function* purchaseVerificationSaga(
    qRCodeData,
    card,
    paymentTotal,
    locationId,
    orderId,
    reservationId,
    confirmationNumber,
    orderEmailAddress,
    item
) {
    yield put(setPixQRCodeData(qRCodeData))
    yield put(initPayment(card, paymentTotal, locationId, orderId))

    const currentLocation = yield select(locationDetailsSelector)
    const userId = yield select(userAccountIdSelector)

    const { type, error } = yield take([PAYMENT_SUCCESS, PAYMENT_CANCELLED, PAYMENT_ERROR])

    yield put(getReservationConfirmation(reservationId, confirmationNumber, orderEmailAddress))
    if (type === PAYMENT_SUCCESS) {
        const parkingMode = yield select(searchModeSelector)
        const currentCustomerFlow = yield select(currentCustomerFlowSelector)
        const flowType = getAnalyticsFlowType(parkingMode, currentCustomerFlow)
        const paymentMethodType = getPaymentMethod(card?.id)

        if (item) {
            const promoCode = item?.promoCode?.barcode
            if (isAmplitudeInitialized()) {
                const purchase = new Revenue()
                    .setProductId(item?.rate?.rateId) // The main ID for the transaction
                    .setPrice(paymentTotal) // The unit price
                    .setQuantity(1) // Number of items
                    .setRevenueType('Purchase') // Categorizes the transaction
                purchase.setEventProperties({
                    'Transaction ID': confirmationNumber,
                    Coupon: promoCode || '',
                    'Flow Type': flowType,
                    'Payment Method': paymentMethodType,
                    'Google Reserve': Boolean(getRwgToken()),
                })
                revenue(purchase)
            }
            sendGTAGEvent(
                'purchase',
                {
                    transaction_id: confirmationNumber,
                    value: paymentTotal,
                    currency: item?.rate?.currencySymbol,
                    coupon: promoCode || null,
                    payment_method: paymentMethodType,
                    items: [
                        {
                            item_id: item?.rate?.rateId,
                            item_name: item?.rate?.rateName,
                            coupon: promoCode || null,
                            quantity: 1,
                        },
                    ],
                    flow_type: flowType,
                },
                userId
            )
        }
        yield take(GET_RESERVATION_CONFIRMATION_RESPONSE)
        yield put(refreshAuthenticatedUser())
        yield put(actions.purchaseCompleted(paymentTotal, orderEmailAddress))
        if (featureToggles.caPurchaseFlow) {
            yield put(sendGoogleReserveConversionData(currentLocation.grsLocation))
        }

        // Wait for navigation (first LOCATION_CHANGE - to pix page or confirmation)
        yield take(LOCATION_CHANGE)

        // If we have qRCodeData, we're going to pix page - wait for one more LOCATION_CHANGE
        // (when user leaves pix page) before clearing the state
        if (qRCodeData) {
            yield take(LOCATION_CHANGE)
        }

        yield put(actions.resetBookingState())
    } else if (type === PAYMENT_ERROR) throw new Error(error)
}

function* purchaseSaga() {
    while (true) {
        const {
            singleItem,
            inboundNumber,
            outboundNumber,
            additionalServices,
            guestUserPersonalData,
            amendReservationId,
        } = yield take(constants.MAKE_PURCHASE)

        let guestUserReservations
        const card = yield select(selectedCreditCardSelector)
        const selectedUpSellOption = yield select(selectedUpSellOptionSelector)

        const userId = yield select(userAccountIdSelector)

        if (amendReservationId) {
            guestUserReservations = yield select(reservationByIdSelector(amendReservationId))
        }
        const guestUserVerifiedData = yield select(guestUserCardVerifiedSelector)
        const selectedVehicleDetails = yield select(selectedVehicleDetailsSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        try {
            yield call(indicateProgress, { names: ['makePurchase'], clearPending: true }, function* () {
                const user = yield select(userSelector)

                const items = [singleItem]
                const paymentTotal = items[0].promoCode ? items[0].promoCode.discountTotal : items[0].rate.amount
                const locationId = items[0].rate.eDataLocationId
                const boletoData = singleItem.boletoData

                const commonPayload = {
                    inboundNumber: inboundNumber,
                    outboundNumber: outboundNumber,
                    apiLanguageCode: apiLanguageCode,
                    card: window.PAYMENT_SERVICE_PROVIDER === 'INGENICO' ? undefined : card,
                }

                switch (card?.id) {
                    case GOOGLE_PAY_METHOD:
                        commonPayload.googlePayFields = yield call(getGooglePayFieldsForBookingPurchase, paymentTotal)
                        commonPayload.GOOGLE_PAY = true
                        commonPayload.card = undefined
                        commonPayload.paymentMethod = GOOGLE_PAY_METHOD
                        break
                    case PIX_PAYMENT_METHOD:
                        commonPayload.PIX = true
                        commonPayload.card = undefined
                        commonPayload.paymentMethod = PIX_PAYMENT_METHOD

                        break
                    case PSE_PAYMENT_METHOD:
                        commonPayload.PSE = card.bankCode
                        commonPayload.card = undefined
                        commonPayload.paymentMethod = PSE_PAYMENT_METHOD
                        break
                    default:
                        commonPayload.paymentMethod = undefined
                        break
                }

                // Fetched after Google Pay so the token doesn't expire while the payment sheet is open
                commonPayload.recaptchaToken = yield call(getRecaptchaToken)

                const payload = {
                    ...commonPayload,
                    token: user?.token,
                    items: items,
                    userDetails: user?.details || guestUserPersonalData?.userPersonalData,
                    selectedVehicleDetails: selectedVehicleDetails || guestUserPersonalData?.vehicle,
                    boletoData: boletoData,
                    startedWithProductId: selectedUpSellOption?.startedWithProductId,
                    additionalServices: additionalServices,
                    reservationId: guestUserReservations?.reservationId,
                }

                const guestUserPayload = {
                    ...commonPayload,
                    token: guestUserVerifiedData?.token,
                    nuveiSessionToken: guestUserVerifiedData?.nuveiSessionToken,
                    bin: guestUserVerifiedData?.bin,
                    items: items[0],
                    userDetails: guestUserPersonalData?.userPersonalData,
                    selectedVehicleDetails: guestUserPersonalData?.vehicle,
                }

                if (userId && amendReservationId) {
                    //this data exists only when user comes to booking flow from manage-my-booking flow, in other case we don't have guestUserReservations.reservationId , what means user is in usual booking or purchase flow
                    const response = yield call(api.editReservation, payload)

                    const { reservationId, confirmationNumber, orderEmailAddress } = response

                    yield call(
                        purchaseVerificationSaga,
                        null,
                        card,
                        paymentTotal,
                        locationId,
                        null,
                        reservationId,
                        confirmationNumber,
                        orderEmailAddress
                    )
                } else {
                    let response
                    // amendReservationId = existing reservation ID when user/guest amends from "manage my booking"
                    if (!user && !amendReservationId && guestUserPersonalData) {
                        response = yield call(api.createGuestUserReservation, guestUserPayload)
                    } else if (!user && amendReservationId) {
                        response = yield call(api.editGuestUserReservation, payload)
                    } else if (user && !amendReservationId) {
                        response = yield call(api.createReservation, payload)
                    } else {
                        throw new CreateReservationError('Purchase branch not matched')
                    }

                    const {
                        orderId,
                        reservationId,
                        qRCodeData,
                        challengeURL3DS,
                        challengeData3DS,
                        confirmationNumber,
                        orderEmailAddress,
                        redirectPaymentLink,
                    } = response

                    if (redirectPaymentLink) {
                        window.location.href = redirectPaymentLink
                        return
                    }

                    if (challengeURL3DS) {
                        yield put(actions.verify3ds(challengeURL3DS, challengeData3DS))

                        const { payload } = yield take(constants.DISCARD_3DS)

                        // User cancelled(in 3ds) or 3ds verification failed
                        if (payload.type === constants.STATUS_3DS_FAILURE) {
                            throw new CreateReservationError(payload?.errorMsg)
                        } else if (payload.type === constants.STATUS_3DS_CLOSED) {
                            // User closed 3ds modal
                            return
                        }

                        yield call(
                            purchaseVerificationSaga,
                            qRCodeData,
                            card,
                            paymentTotal,
                            locationId,
                            orderId,
                            reservationId,
                            undefined,
                            undefined,
                            items[0]
                        )
                    } else {
                        yield call(
                            purchaseVerificationSaga,
                            qRCodeData,
                            card,
                            paymentTotal,
                            locationId,
                            orderId,
                            reservationId,
                            confirmationNumber,
                            orderEmailAddress,
                            items[0]
                        )
                    }
                }
            })
        } catch (exception) {
            yield put(actions.purchaseError())
        }
    }
}

export function* watchGetSeasonTicketsSaga() {
    yield takeEvery(grs.constants.GET_SEASON_TICKETS, function* ({ locationId, startDate }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            yield call(indicateProgress, 'getSeasonTickets', function* () {
                const tickets = yield call(grs.api.fetchSeasonTickets, locationId, apiLanguageCode, startDate)
                yield put(grs.actions.receivedSeasonTickets(tickets))
            })
        } catch (error) {}
    })
}

export function* watchSelectSeasonTicketSaga() {
    yield takeEvery(constants.SELECT_SEASON_TICKET, function* ({ ticket, grsId, startDate, waitingListKey }) {
        if (ticket.isOnWaitingList && !waitingListKey) return

        try {
            const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))
            const apiLanguageCode = yield select(apiLanguageCodeSelector)

            const details = yield call(indicateProgress, 'getSeasonTicketRate', grs.api.getRateDetails, {
                locationId: grsId,
                rateId: ticket.id || ticket.rateId,
                beginTime: startDate || ticket.nextAvailableDateISO || new Date(),
                endTime: null,
                waitingListKey,
                apiLanguageCode,
                customerFlowType: currentCustomerFlowType,
            })

            yield put(actions.receivedSelectedSeasonTicketRate(details))
        } catch (error) {}
    })
}

function* watchWaitingListSaga() {
    const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))

    yield takeEvery(grs.constants.VALIDATED_WAITING_LIST_KEY, function* ({ waitingListKey, response }) {
        const { rateId, locationId, fromDate } = response
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        const rate = yield call(indicateProgress, 'getWaitingListRate', grs.api.getRateDetails, {
            locationId,
            rateId,
            fromDate,
            endTime: null,
            waitingListKey,
            apiLanguageCode,
            customerFlowType: currentCustomerFlowType,
        })

        yield put(actions.receivedWaitingListRate(rate))
    })
}

export function* getUpsellOptionsSaga() {
    yield takeEvery(constants.GET_UPSELL_OPTIONS, function* ({ rateId, lotId, beginTime, endTime }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const response = yield call(
                indicateProgress,
                'getUpSellOptions',
                grs.api.getUpSellOptions,
                rateId,
                lotId,
                beginTime,
                endTime,
                apiLanguageCode
            )
            yield put(actions.setUpSellOptions(response))
        } catch (exception) {
            yield put(actions.failedToFetchUpSellOptions(exception))
        }
    })
}

export function* getAdditionParkingItemsSaga() {
    yield takeEvery(constants.GET_ADDITIONAL_PARKING_ITEMS, function* ({ rateId, lotId }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const response = yield call(
                indicateProgress,
                constants.GET_ADDITIONAL_PARKING_ITEMS,
                grs.api.getAdditionalParkingItems,
                rateId,
                lotId,
                apiLanguageCode
            )
            yield put(actions.setAdditionalParkingItems(response))
        } catch (exception) {
            yield put(actions.getAdditionalParkingItemsError(exception))
        }
    })
}

export function* upgradeUpSellRateSaga() {
    yield takeEvery(
        constants.UPGRAGE_UPSELL_RATE,
        function* ({ locationId, rateId, beginTime, endTime, startedWithProductId }) {
            try {
                const apiLanguageCode = yield select(apiLanguageCodeSelector)
                const currentCustomerFlowType = customerFlowType(yield select(searchModeSelector))

                const details = yield call(indicateProgress, 'upgradeUpSellOption', grs.api.getRateDetails, {
                    locationId,
                    rateId,
                    beginTime,
                    endTime,
                    apiLanguageCode,
                    customerFlowType: currentCustomerFlowType,
                    startedWithProductId,
                })
                yield put(
                    actions.setLastBookingDetails({ grsId: details.locationId, rateId: details.rateId, rate: details })
                )
                yield put(actions.selectRate(details))
            } catch (exception) {}
        }
    )
}

export default function* rootSaga() {
    yield fork(watchGetRates)
    yield fork(watchSelectRate)
    yield fork(watchRemoveVehicle)
    yield fork(watchGetBookingLocationDetails)
    yield fork(promoCodeSaga)
    yield fork(purchaseSaga)
    yield fork(getUpsellOptionsSaga)
    yield fork(upgradeUpSellRateSaga)
    yield fork(getBookingLocationDetailsMonthlyParking)
    yield fork(watchGetSeasonTicketsSaga)
    yield fork(watchSelectSeasonTicketSaga)
    yield fork(watchWaitingListSaga)
    yield fork(watchSetEvent)
    yield fork(watchFetchEventRate)
    yield fork(getAdditionParkingItemsSaga)
}
